import React, { useMemo, memo } from 'react';
import zxcvbn from 'zxcvbn';
import { STRENGTH_LEVELS, PASSWORD_CONFIG } from '../../constants/security';
import { useTranslation } from '../../hooks/useTranslationSimple';
import './PasswordStrengthAnalyzer.css';

/**
 * 패스워드 강도 분석 컴포넌트
 * zxcvbn 라이브러리를 사용하여 실시간 패스워드 강도 분석
 */
const PasswordStrengthAnalyzer = memo(function PasswordStrengthAnalyzer({ password, userInputs = [], showDetails = true }) {
  const { t } = useTranslation();
  
  // 패스워드 강도 분석 (메모이제이션으로 성능 최적화)
  const analysis = useMemo(() => {
    if (!password || password.length === 0) {
      return null;
    }
    
    return zxcvbn(password, userInputs);
  }, [password, userInputs]);

  // 분석 결과가 없으면 렌더링하지 않음
  if (!analysis) {
    return null;
  }

  const { score, feedback, crack_times_display, guesses_log10 } = analysis;

  const currentLevel = STRENGTH_LEVELS[score];

  // 진행률 계산 (0-100%)
  const progressPercentage = ((score + 1) / 5) * 100;

  // 개선 권장사항 생성
  const getRecommendations = () => {
    const recommendations = [];
    
    if (feedback.warning) {
      recommendations.push({
        type: 'warning',
        message: feedback.warning
      });
    }

    feedback.suggestions?.forEach(suggestion => {
      recommendations.push({
        type: 'suggestion',
        message: suggestion
      });
    });

    // 추가 권장사항
    if (password.length < PASSWORD_CONFIG.RECOMMENDED_MIN_LENGTH) {
      recommendations.push({
        type: 'suggestion',
        message: t('strength.lengthRecommendation', { length: PASSWORD_CONFIG.RECOMMENDED_MIN_LENGTH })
      });
    }

    if (!/[A-Z]/.test(password)) {
      recommendations.push({
        type: 'suggestion',
        message: t('strength.addUppercase')
      });
    }

    if (!/[a-z]/.test(password)) {
      recommendations.push({
        type: 'suggestion',
        message: t('strength.addLowercase')
      });
    }

    if (!/[0-9]/.test(password)) {
      recommendations.push({
        type: 'suggestion',
        message: t('strength.addNumbers')
      });
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      recommendations.push({
        type: 'suggestion',
        message: t('strength.addSpecialChars')
      });
    }

    return recommendations;
  };

  const recommendations = getRecommendations();

  return (
    <div className="password-strength-analyzer">
      {/* 강도 표시 바 */}
      <div className="strength-bar-container">
        <div className="strength-bar">
          <div 
            className="strength-progress"
            style={{
              width: `${progressPercentage}%`,
              backgroundColor: currentLevel.color
            }}
          />
        </div>
        <div className="strength-label" style={{ color: currentLevel.color }}>
          {currentLevel.label}
        </div>
      </div>

      {/* 상세 정보 표시 */}
      {showDetails && (
        <div className="strength-details">
          {/* 크랙 시간 정보 */}
          <div className="crack-time-info">
            <div className="info-item">
              <span className="info-label">{t('strength.onlineAttack')}:</span>
              <span className="info-value">{crack_times_display.online_throttling_100_per_hour}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('strength.offlineAttack')}:</span>
              <span className="info-value">{crack_times_display.offline_slow_hashing_1e4_per_second}</span>
            </div>
            <div className="info-item">
              <span className="info-label">{t('strength.guessCount')} (log10):</span>
              <span className="info-value">{Math.round(guesses_log10 * 100) / 100}</span>
            </div>
          </div>

          {/* 권장사항 */}
          {recommendations.length > 0 && (
            <div className="recommendations">
              <h4>{t('strength.recommendations')}:</h4>
              <ul>
                {recommendations.map((rec, index) => (
                  <li key={index} className={`recommendation-${rec.type}`}>
                    {rec.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default PasswordStrengthAnalyzer;